import { useState, useEffect, useCallback } from 'react';

import { useRouter } from 'next/router';
import { isMatchingPath } from '../utils/routeUtils';
import { useBookingPath } from '../graphql';
import { useUserContext } from '@g4/next-furniture/UserContextProvider';

const initialBreadcrumbSteps = [
  {
    key: 'flights',
    path: '/flights',
  },
  {
    key: 'travelers',
    path: '/travelers',
  },
  {
    key: 'seats',
    path: '/seats',
  },
  {
    key: 'bags',
    path: '/ancillaries',
  },
  {
    key: 'hotels',
    path: '/hotels',
  },
  {
    key: 'cars',
    path: '/cars',
  },
  {
    key: 'cartOverride',
    path: '/cart-override',
  },
  {
    key: 'payment',
    path: '/payment',
  },
];

export const breadcrumbSteps = isStadium => {
  if (isStadium) {
    return [
      {
        key: 'tickets',
        path: '/tickets',
      },
      ...initialBreadcrumbSteps,
    ];
  } else {
    return initialBreadcrumbSteps;
  }
};

export const makeStep = (step, transactionId, pathname = '') => {
  const pathnameRoutes = pathname.split('/');
  const lastPath = `/${pathnameRoutes[pathnameRoutes.length - 1]}`;
  const lastButOnePath = `/${pathnameRoutes[pathnameRoutes.length - 2]}`;

  return {
    ...step,
    href:
      step.path === '/tickets'
        ? `/booking/[transactionId]${step.path}?isStadium=true`
        : `/booking/[transactionId]${step.path}`,
    as:
      step.path === '/tickets'
        ? `/booking/${transactionId}${step.path}?isStadium=true`
        : `/booking/${transactionId}${step.path}`,
    isActive:
      isMatchingPath([step.path, lastPath]) ||
      isMatchingPath([step.path, lastButOnePath]),
  };
};

const useBookingSteps = ({ skipSteps = [] } = {}) => {
  const { isAdmin, isStationAgent } = useUserContext();

  if (!isAdmin) {
    skipSteps = [...skipSteps, 'cartOverride'];
  }

  if (isStationAgent) {
    skipSteps = [...skipSteps, 'hotels', 'cars'];
  }

  const { pathname, push, events, query } = useRouter();
  const [isChangingRoute, setIsChangingRoute] = useState(false);
  const { transactionId } = query;

  const { isStadium, isHotelAvailable, isVehicleAvailable } = useBookingPath();
  const stepsList = breadcrumbSteps(isStadium).map(bs => bs.key);

  if (!isHotelAvailable) {
    skipSteps = [...skipSteps, 'hotels'];
  }

  if (!isVehicleAvailable) {
    skipSteps = [...skipSteps, 'cars'];
  }

  const steps = useCallback(
    breadcrumbSteps(isStadium)
      .map(step => makeStep(step, transactionId, pathname))
      .filter(step => !skipSteps.includes(step.key)),
    [breadcrumbSteps, transactionId]
  );

  const activeIndex = steps.findIndex(step => step.isActive);
  const activeStep = steps[activeIndex];
  const nextStep = steps[activeIndex + 1];

  useEffect(() => {
    const enableIsChangingRoute = () => setIsChangingRoute(true);
    const disableIsChangingRoute = () => setIsChangingRoute(false);

    if (process.browser) {
      // Enable isChangingRoute when route change begins
      events.on('routeChangeStart', enableIsChangingRoute);
      // Disable isChangingRoute when route change ends
      events.on('routeChangeComplete', disableIsChangingRoute);
      // Disable isChangingRoute when route change errors
      events.on('routeChangeError', disableIsChangingRoute);

      return () => {
        events.off('routeChangeStart', enableIsChangingRoute);
        events.off('routeChangeComplete', disableIsChangingRoute);
        events.off('routeChangeError', disableIsChangingRoute);
      };
    }
  }, [events]);

  const navigateToStep = useCallback(
    key => {
      const step = steps.find(step => step.key === key);

      if (!step) {
        return;
      }

      // Added force scroll after route change. 'next' > 10 will handle this out of the box.
      // 'then' block can be removed after 'next' upgrade.
      push(
        isStadium ? `${step.href}?isStadium=${isStadium}` : `${step.href}`,
        isStadium ? `${step.as}?isStadium=${isStadium}` : `${step.as}`
      ).then(() => window.scrollTo(0, 0));
    },
    [push, steps, isStadium]
  );

  const navigateToNextStep = useCallback(() => {
    navigateToStep(nextStep.key);
  }, [nextStep, navigateToStep]);

  const shouldRedirect = useCallback(
    lastStepCompleted => {
      if (!lastStepCompleted) {
        return false;
      }
      const lastStepCompletedIndex = stepsList.indexOf(
        lastStepCompleted?.toLowerCase()
      );
      const shouldIRedirect =
        activeIndex - lastStepCompletedIndex > 1 && !!lastStepCompleted;
      const redirect =
        isAdmin && pathname === `/booking/[transactionId]/payment`
          ? false
          : shouldIRedirect; // we are not setting cart-overrid to lastCompletedBookingStep query as it is not implimented in GQL so adding the condintion to load the payment page

      return redirect;
    },
    [activeIndex, stepsList, pathname, isAdmin]
  );

  const redirectTo = useCallback(
    lastStepCompleted =>
      navigateToStep(
        steps[stepsList.indexOf(lastStepCompleted?.toLowerCase()) + 1].key
      ),
    [navigateToStep, steps, stepsList]
  );
  return {
    steps,
    activeIndex,
    activeStep,
    nextStep,
    navigateToStep,
    navigateToNextStep,
    isChangingRoute,
    transactionId,
    shouldRedirect,
    redirectTo,
  };
};

export default useBookingSteps;
